import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  BodyMediumBold,
  BodySmall,
  FormLabelSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {
  Card,
  CardFooter,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {
  Toast,
  TOAST_SEMANTIC,
  ToastTitle,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {useSelector, useDispatch} from 'react-redux';
import {useDrag, useDrop} from 'react-dnd-cjs';
import {selectApplicantInformation} from 'src/selectors/crm';
import css from 'src/components/crm/jobs/job-info/overview/hiring-process/unparsed-resume-panel/all-expreriences/all-expriences.css';
import {Checkbox} from '@spaced-out/ui-design-system/lib/components/Checkbox';
import type {ExperienceType} from 'src/components/crm/jobs/job-info/overview/hiring-process/unparsed-resume-panel/all-expreriences/edit-experience-card';
import type {educationType} from 'src/components/crm/jobs/job-info/overview/hiring-process/unparsed-resume-panel/all-educations/edit-education-card';
import {ExperienceCard} from 'src/components/crm/jobs/job-info/overview/hiring-process/unparsed-resume-panel/all-expreriences/edit-experience-card';
import {ClickableIcon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {TruncatedTextWithTooltip} from '@spaced-out/ui-design-system/lib/components/TruncatedTextWithTooltip';
import {formatExperirnceDate} from 'src/utils/crm';

import * as reduxApi from 'src/utils/redux-api-v2';


export type FormData = {
  name: string;
  email: string;
  phone: string;
  yearsExp: string;
  monthsExp: string;
  location: string;
  experiences: Array<ExperienceType>;
  educations: Array<educationType>;
};
type Props = {
  setFormData: (
    prevData: FormData | ((prevData: FormData) => FormData),
  ) => void;
};

const ITEM_TYPE = 'EXPERIENCE';

export const AllExpriences = ({setFormData}: Props) => {
  const {t, makeKey} = useLabelTranslation();
  const [addExperienceCardOpen, setAddExperienceCardOpen] =
    React.useState(false);
  const [editingIndex, setEditingIndex] = React.useState(-1);

  const applicantDetails = useSelector(selectApplicantInformation);
  const initialExperiences = applicantDetails?.resume?.experiences ?? [];
  const [experiences, setExperiences] = React.useState(initialExperiences);

  const dispatch = useDispatch();

  // @ts-expect-error - TS7006 - Parameter 'index' implicitly has an 'any' type.
  const handleEditClick = (index) => {
    setEditingIndex(index);
    setAddExperienceCardOpen(true);
  };

  const handleAddExperience = () => {
    setEditingIndex(-1);
    setAddExperienceCardOpen(true);
  };

  // @ts-expect-error - TS7006 - Parameter 'toIndex' implicitly has an 'any' type.
  const moveExperience = (fromIndex: any, toIndex) => {
    const updatedExperiences = [...experiences];
    const [movedExperience] = updatedExperiences.splice(fromIndex, 1);
    updatedExperiences.splice(toIndex, 0, movedExperience);
    setExperiences(updatedExperiences);
    dispatch(
      reduxApi.post(
        `api/candidates/${applicantDetails.id}`,
        {
          section: 'EXPERIENCE_LIST',
          editFields: {data: updatedExperiences, type: 'EDIT'},
        },
        {},
        {apiPath: '/trmapi/'},
      ),
    )
      .then((res) => {
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.success}>
            <ToastTitle>{`${t(
              'Experience_Updated_Successfully',
              'Experience Updated Successfully',
            )}!`}</ToastTitle>
          </Toast>,
        );
      })
      .catch((err) => {
        toastApi.show(
          <Toast semantic={TOAST_SEMANTIC.danger}>
            <ToastTitle>{`${t(
              'Error_Updating_Experience',
              'Error Updating Experience',
            )}!`}</ToastTitle>
          </Toast>,
        );
      });
  };

  React.useEffect(() => {
    // @ts-expect-error - TS2345 - Argument of type '(prevData: FormData) => { experiences: EXPERIENCE[]; name: string; email: string; phone: string; yearsExp: string; monthsExp: string; location: string; educations: educationType[]; }' is not assignable to parameter of type 'FormData | ((prevData: FormData) => FormData)'.
    setFormData((prevData) => ({
      ...prevData,
      experiences,
    }));
  }, [experiences, setFormData]);

  // @ts-expect-error - TS7031 - Binding element 'experience' implicitly has an 'any' type. | TS7031 - Binding element 'index' implicitly has an 'any' type.
  const ExperienceCardDraggable = ({experience, index}) => {
    const ref = React.useRef(null);
    // @ts-expect-error - TS2339 - Property 'isDragging' does not exist on type 'unknown'.
    const [{isDragging}, drag, preview] = useDrag({
      // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'type' does not exist in type 'DragSourceHookSpec<{ type: string; index: any; }, unknown, { isDragging: boolean; }>'.
      type: ITEM_TYPE,
      item: {type: ITEM_TYPE, index},
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    const [, drop] = useDrop({
      accept: ITEM_TYPE,
      drop: (item) => {
        if (!ref.current) {
          return;
        }
        // @ts-expect-error - TS2339 - Property 'index' does not exist on type 'DragObjectWithType'.
        if (item.index !== index) {
          // @ts-expect-error - TS2339 - Property 'index' does not exist on type 'DragObjectWithType'.
          moveExperience(item.index, index);
          // @ts-expect-error - TS2339 - Property 'index' does not exist on type 'DragObjectWithType'.
          item.index = index;
        }
      },
    });

    preview(drop(ref));

    return (
      <Card
        ref={ref}
        key={index}
        classNames={{wrapper: css.experienceCard}}
        // @ts-expect-error - TS2769 - No overload matches this call.
        style={{opacity: isDragging ? 0.5 : 1}}
      >
        <div>
          <ClickableIcon
            color="primary"
            name="grip-dots-vertical"
            type="regular"
            // @ts-expect-error - TS2769 - No overload matches this call.
            ref={drag}
          />
        </div>
        <div className={css.experienceCardDetails}>
          <div className={css.experienceDetails}>
            <BodyMediumBold>
              {/* @ts-expect-error - TS2322 - Type '{ children: any; tooltipText: { bodyMaxLines: number; }; }' is not assignable to type 'IntrinsicAttributes & TruncatedTextWithTooltipProps'. */}
              <TruncatedTextWithTooltip tooltipText={{bodyMaxLines: 4}}>
                {experience.experienceOrg || ''}
              </TruncatedTextWithTooltip>
            </BodyMediumBold>
            <BodySmall>
              {/* @ts-expect-error - TS2322 - Type '{ children: any; tooltipText: { bodyMaxLines: number; }; }' is not assignable to type 'IntrinsicAttributes & TruncatedTextWithTooltipProps'. */}
              <TruncatedTextWithTooltip tooltipText={{bodyMaxLines: 4}}>
                {experience.title || ''}
              </TruncatedTextWithTooltip>
            </BodySmall>
          </div>
          <div className={css.experienceDetails}>
            <BodyMediumBold>
              {/* @ts-expect-error - TS2322 - Type '{ children: string[]; tooltipText: { bodyMaxLines: number; }; }' is not assignable to type 'IntrinsicAttributes & TruncatedTextWithTooltipProps'. */}
              <TruncatedTextWithTooltip tooltipText={{bodyMaxLines: 4}}>
                {formatExperirnceDate(
                  experience.startMonth,
                  experience.startYear,
                  t,
                  makeKey,
                )}{' '}
                -{' '}
                {experience.isCurrent
                  ? t('Present', 'Present')
                  : formatExperirnceDate(
                      experience.endMonth,
                      experience.endYear,
                      t,
                      makeKey,
                    )}
              </TruncatedTextWithTooltip>
            </BodyMediumBold>
            <BodySmall>
              {/* @ts-expect-error - TS2322 - Type '{ children: any; tooltipText: { bodyMaxLines: number; }; }' is not assignable to type 'IntrinsicAttributes & TruncatedTextWithTooltipProps'. */}
              <TruncatedTextWithTooltip tooltipText={{bodyMaxLines: 4}}>
                {experience.location || ''}
              </TruncatedTextWithTooltip>
            </BodySmall>
          </div>
        </div>
        <div>
          <Button
            iconRightName="pen"
            type="ghost"
            onClick={() => handleEditClick(index)}
          />
        </div>
      </Card>
    );
  };

  return (
    <div className={css.resumeFieldsContainer}>
      <div className={css.header}>
        <FormLabelSmall color="secondary">
          {t('Work_Experience', 'Work Experience')}
        </FormLabelSmall>
        {experiences?.length !== 0 && (
          <Button
            iconLeftName="plus"
            type="secondary"
            onClick={handleAddExperience}
          >
            {t('Add_Experience', 'Add Experience')}
          </Button>
        )}
      </div>
      <div className={css.emptyStateContainer}>
        {!experiences || experiences.length === 0 ? (
          addExperienceCardOpen ? (
            <ExperienceCard
              setAddExperienceCardOpen={setAddExperienceCardOpen}
              // @ts-expect-error - TS2322 - Type 'EXPERIENCE[]' is not assignable to type 'ExperienceType[]'.
              experiences={experiences}
              // @ts-expect-error - TS2322 - Type 'Dispatch<SetStateAction<EXPERIENCE[]>>' is not assignable to type '(experiences: ExperienceType[]) => void'.
              setExperiences={setExperiences}
            />
          ) : (
            <Card>
              <EmptyState
                description={`${t(
                  'Sorry_we_couldn\'t_fetch_this_candidate\'s_experience_Please_fill_it_in_manually',
                  'Sorry, we couldn\'t fetch this candidate\'s experience. Please fill it in manually',
                )}.`}
                imageVariant="data"
              >
                <Button
                  iconLeftName="plus"
                  type="secondary"
                  onClick={handleAddExperience}
                >
                  {t('Add_Experience', 'Add Experience')}
                </Button>
              </EmptyState>
            </Card>
          )
        ) : (
          <div className={css.experienceWrapper}>
            {addExperienceCardOpen && editingIndex === -1 && (
              <div className={css.resumeFieldsContainer}>
                <ExperienceCard
                  setAddExperienceCardOpen={setAddExperienceCardOpen}
                  // @ts-expect-error - TS2322 - Type 'EXPERIENCE[]' is not assignable to type 'ExperienceType[]'.
                  experiences={experiences}
                  // @ts-expect-error - TS2322 - Type 'Dispatch<SetStateAction<EXPERIENCE[]>>' is not assignable to type '(experiences: ExperienceType[]) => void'.
                  setExperiences={setExperiences}
                />
              </div>
            )}
            <div className={css.experienceCardContainer}>
              {experiences.map((experience, index) =>
                editingIndex === index && addExperienceCardOpen ? (
                  <>
                    <ExperienceCard
                      setAddExperienceCardOpen={setAddExperienceCardOpen}
                      // @ts-expect-error - TS2322 - Type 'EXPERIENCE[]' is not assignable to type 'ExperienceType[]'.
                      experiences={experiences}
                      // @ts-expect-error - TS2322 - Type 'Dispatch<SetStateAction<EXPERIENCE[]>>' is not assignable to type '(experiences: ExperienceType[]) => void'.
                      setExperiences={setExperiences}
                      editIndex={editingIndex}
                    />
                  </>
                ) : (
                  <ExperienceCardDraggable
                    key={index}
                    experience={experience}
                    index={index}
                  />
                ),
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
